import NaviNewLogoIconWithText from '@navi/web-ui/lib/icons/NaviLogoIcon/NaviNewLogoIconWithText';
import { Avatar, Card, ModalDialog, Typography } from '@navi/web-ui/lib/primitives';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import styles from './index.module.scss';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from 'src/components/SkeletonLoader/LineLoader';
import { useEffect, useState } from 'react';
import RejectionModal from './RejectionModal';
import { formatDate } from 'src/utils/commonUtils';
import { ISelectedApprovalItem } from '../../interface';
import Actions from '../Actions';
import NoResult from '../NoResult';

const NAVI_AGENCY_ID = '1000';
const IdCard = (props: ISelectedApprovalItem) => {
  const { handleApproval, handleRejection } = props;
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showExpandImageModal, setShowExpandImageModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const { totalIdCardPendingApprovals, selectedIdApprovalItem, loading, idCardValidAsOf } =
    useSelector((state: RootState) => state.approvals);
  const timestamp = selectedIdApprovalItem?.createdAt;
  const { agencyName, name, phoneNumber, agencyCode } = selectedIdApprovalItem?.makerDetails || {};
  const { originalDocumentUri, optimizedDocumentUri } = selectedIdApprovalItem?.data || {};

  const isCardLoading = loading && !selectedIdApprovalItem;

  const toggleRejectionModal = () => setShowRejectionModal(prev => !prev);

  const toggleImageExpandModal = () => {
    setShowExpandImageModal(prev => !prev);
  };

  useEffect(() => {
    if (originalDocumentUri) {
      setImageLoading(true);
    }
  }, [originalDocumentUri]);

  if (!loading && !selectedIdApprovalItem) {
    return <NoResult totalPendingApprovals={totalIdCardPendingApprovals} />;
  }

  return (
    <div key={timestamp} className={styles.selectedIdCard}>
      <Card className={styles.card}>
        <NaviNewLogoIconWithText className={styles.logo} />
        <SuspenseLoader
          loading={isCardLoading}
          fallBack={
            <LineLoader lineWidth={150} dimenshionUnit="px" numberOfLines={2} childrenType={'P2'} />
          }
        >
          {agencyName && agencyCode !== NAVI_AGENCY_ID ? (
            <>
              <Typography variant="p4" className={styles.timstampText}>
                in association with
              </Typography>
              <Typography variant="h4">{agencyName}</Typography>
            </>
          ) : null}
        </SuspenseLoader>
        <SuspenseLoader
          loading={isCardLoading}
          fallBack={<LineLoader lineStyle={styles.imageLoader} />}
        >
          <div className={styles.avatarContainer} onClick={toggleImageExpandModal}>
            <SuspenseLoader
              loading={imageLoading}
              fallBack={
                <>
                  <img className={styles.avatar} src={optimizedDocumentUri} loading="eager" />
                  <img
                    className={styles.hiddenImage}
                    src={originalDocumentUri}
                    onLoad={() => setImageLoading(false)}
                    loading="eager"
                  />
                </>
              }
            >
              <Avatar
                isImage
                src={originalDocumentUri}
                size={182}
                loading={'lazy'}
                className={styles.avatar}
              />
            </SuspenseLoader>
            <div className={styles.avatarTextContainer}>
              <Typography variant="h6" className={styles.avatarText}>
                View
              </Typography>
            </div>
          </div>
        </SuspenseLoader>
        <SuspenseLoader
          loading={isCardLoading}
          fallBack={
            <LineLoader lineWidth={150} dimenshionUnit="px" numberOfLines={3} childrenType={'P2'} />
          }
        >
          {idCardValidAsOf ? (
            <Typography variant="p4" className={styles.timstampText}>
              Valid as of {formatDate(idCardValidAsOf)}
            </Typography>
          ) : null}

          <Typography variant="h2" className={styles.name}>
            {name}
          </Typography>
          <Typography variant="h5" className={styles.number}>
            Debt Management Executive
          </Typography>
          <Typography variant="h3" className={styles.number}>
            {phoneNumber}
          </Typography>
        </SuspenseLoader>
      </Card>
      <Actions
        isLoading={isCardLoading}
        handleApproval={handleApproval}
        toggleRejectionModal={toggleRejectionModal}
      />
      <ModalDialog
        open={showRejectionModal}
        onClose={toggleRejectionModal}
        header="Select Rejection reason"
      >
        <RejectionModal handleRejection={handleRejection} closeModal={toggleRejectionModal} />
      </ModalDialog>
      <ModalDialog open={showExpandImageModal} onClose={toggleImageExpandModal} header={name}>
        <img src={originalDocumentUri} alt="id-image" className={styles.expandedImage} />
      </ModalDialog>
    </div>
  );
};

export default IdCard;
